import type { UseMutationResult } from '@tanstack/react-query';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { useApplicationFetchers } from '../../fetchers/use-application-fetchers';
import { jobArchitectureKeys } from './query-keys';

type UnpublishJobItemVersionParams = {
    readonly architectureId: string;
    readonly itemId: string;
    readonly itemType: 'job-level' | 'job-role';
    readonly version: string;
};

export const useUnpublishJobItemVersion = (): UseMutationResult<unknown, Error, UnpublishJobItemVersionParams> => {
    const { unpublishJobLevelVersion, unpublishJobRoleVersion } = useApplicationFetchers();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({ architectureId, itemId, itemType, version }) => {
            const result =
                itemType === 'job-level'
                    ? await unpublishJobLevelVersion({
                          jobArchitectureId: architectureId,
                          jobLevelId: itemId,
                          version,
                      })
                    : await unpublishJobRoleVersion({
                          jobArchitectureId: architectureId,
                          jobRoleId: itemId,
                          version,
                      });

            if (result.isErr) {
                throw result.error;
            }

            return result.value;
        },
        onSuccess: (_, { architectureId }) => {
            queryClient.invalidateQueries({ queryKey: jobArchitectureKeys.architecture(architectureId) });
            queryClient.invalidateQueries({ queryKey: jobArchitectureKeys.jobProfileManagement() });
        },
    });
};
